import { captureWalletEvent, eventError, eventId, ownerForAddress } from "@shared/analytics/walletEvents";
import { BigDecimal } from "@solomon-labs/bigdecimal";
import type { PublicKey } from "@solomon-labs/publickey";
import { useEffect, useRef, useState } from "react";

import { simulateStartUnstake } from "@/services/staking/simulateStartUnstake";
import { migrationContext } from "@/services/usdv-migration/analytics";

const DUST_LIMIT = new BigDecimal("0.01");

interface SimulationState {
    key: string;
    canUnstake: boolean;
}

/**
 * Balances at or above one cent are shown directly. Smaller balances stay
 * hidden until the real start-unstake instruction passes RPC simulation.
 */
export function useCanUnstakeSusdv(
    owner: PublicKey | null,
    sharesAmount: BigDecimal,
    onResolved?: (result: "eligible" | "ineligible" | "error") => void
): boolean {
    const resolvedRef = useRef(onResolved);
    resolvedRef.current = onResolved;
    const ownerAddress = owner?.toString() ?? null;
    const amount = sharesAmount.toString();
    const needsSimulation = sharesAmount.gt(0) && sharesAmount.lt(DUST_LIMIT);
    const simulationKey = `${ownerAddress ?? "disconnected"}:${amount}`;
    const [simulation, setSimulation] = useState<SimulationState | null>(null);
    const ownerRef = useRef(owner);
    ownerRef.current = owner;

    useEffect(() => {
        const simulationOwner = ownerRef.current;
        if (!simulationOwner || !needsSimulation) return;

        const eventOwner = ownerForAddress(simulationOwner.toString());
        const check = {
            ...migrationContext(simulationOwner.toString()),
            check_id: eventId(),
            check_type: "eligibility" as const,
            trigger: "initial"
        };
        captureWalletEvent("migration:check_started", check, eventOwner);
        const abortController = new AbortController();
        let active = true;
        setSimulation({ key: simulationKey, canUnstake: false });
        void simulateStartUnstake(simulationOwner, new BigDecimal(amount), abortController.signal)
            .then((canUnstake) => {
                if (active) {
                    captureWalletEvent(
                        "migration:check_completed",
                        { ...check, result: canUnstake ? "eligible" : "ineligible" },
                        eventOwner
                    );
                    resolvedRef.current?.(canUnstake ? "eligible" : "ineligible");
                    setSimulation({ key: simulationKey, canUnstake });
                }
            })
            .catch((error: unknown) => {
                if (active) {
                    captureWalletEvent("migration:check_failed", { ...check, ...eventError(error) }, eventOwner);
                    resolvedRef.current?.("error");
                    setSimulation({ key: simulationKey, canUnstake: false });
                }
            });

        return (): void => {
            active = false;
            abortController.abort();
        };
    }, [amount, needsSimulation, ownerAddress, simulationKey]);

    if (!sharesAmount.gt(0) || !owner) return false;
    if (sharesAmount.gte(DUST_LIMIT)) return true;
    return simulation?.key === simulationKey && simulation.canUnstake;
}
